import ButtonT from '@components/tailwind-components/Button/ButtonT';
import CheckboxTogglerT from '@components/tailwind-components/Input/CheckboxTogglerT';
import type React from 'react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getTimeIntervalValidationSchema } from '../../../../../helpers/validationSchemaHelpers';
import { useModal } from '../../../../../hooks/modal/useModal';
import { useAvailabilities } from '../../../../../hooks/settings/handover/useAvailabilites';
import { useAvailabilitiesAction } from '../../../../../hooks/settings/handover/useAvailabilitiesAction';
import { useValidationForm } from '../../../../../hooks/useValidationForm';
import type { FormDataIntervals, Interval } from '../../Calendar.types';
import { applyToDay, applyToMultipleDays } from '../../Calendar.utils';
import IntervalInputField from './IntervalInputField';
import MultipleDaySelector from './MultipleDaySelector';

export const EditDayModal: React.FC = () => {
  const { t } = useTranslation();
  const { selectedDate, availabilities, currentAvailabilities } =
    useAvailabilities();
  const { updateAvailabilities } = useAvailabilitiesAction();

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    validationErrors,
    clearErrors,
  } = useValidationForm<FormDataIntervals>({
    schema: getTimeIntervalValidationSchema(),
    defaultValues: {
      newAvailabilities: currentAvailabilities,
      weekDays: selectedDate ? [selectedDate] : [],
      applyToMultiple: false,
    },
    shouldUseNativeValidation: true,
  });

  const { hideModal } = useModal();

  const updateAvailabilitiesByType = useCallback(
    (data: FormDataIntervals) => {
      const { newAvailabilities, weekDays, applyToMultiple } = data;
      newAvailabilities.forEach((item: Interval) => {
        item.recurring = undefined;
      });

      if (applyToMultiple && weekDays) {
        return updateAvailabilities(
          applyToMultipleDays(availabilities, newAvailabilities, weekDays),
        );
      }

      if (!selectedDate) {
        return;
      }

      return updateAvailabilities(
        applyToDay(availabilities, newAvailabilities, selectedDate),
      );
    },
    [availabilities, selectedDate, updateAvailabilities],
  );

  const hideAndSubmit = useCallback(
    async (data: FormDataIntervals) => {
      await updateAvailabilitiesByType(data);
      hideModal();
    },
    [hideModal, updateAvailabilitiesByType],
  );

  const applyToMultiple = watch('applyToMultiple');

  return (
    <form
      className='tailwind-modal-form'
      onSubmit={handleSubmit(hideAndSubmit)}>
      <div className='tailwind-modal-body'>
        <div className='grid grid-cols-1 gap-x-6 gap-y-2'>
          <IntervalInputField
            clearErrors={clearErrors}
            errors={validationErrors || {}}
            control={control}
            name={'newAvailabilities'}
            watch={watch}
            setValue={setValue}
          />
          <CheckboxTogglerT
            toggleSize='large'
            label={t('handover-settings.apply-to-multiple')}
            field='applyToMultiple'
            control={control}
          />
          {applyToMultiple && (
            <MultipleDaySelector control={control} name={'weekDays'} />
          )}
        </div>
      </div>
      <div className='tailwind-modal-footer'>
        <ButtonT
          type='button'
          styling='cancel'
          size='xs'
          rounding='md'
          onClick={hideModal}>
          {t('general.buttons.cancel')}
        </ButtonT>
        <ButtonT type='submit' styling='primary' size='xs' rounding='md'>
          {t('general.buttons.save')}
        </ButtonT>
      </div>
    </form>
  );
};

export default EditDayModal;
